import moment from 'moment';
import { LOCATION_CHANGE } from 'connected-react-router';
import appLogger from 'Utils/logging';

import { createAndUpdateChannelAction, publishVideoToChannelAction } from '../Reducks/Channel/actions';
import { get } from '../Utils/objectHelpers';
import { getApplicationState, setApplicationState } from '../Reducks/AppState/actions';
import { getMessagesByTag } from '../Reducks/MessageCenterNotification/actions';
import { getOrganizerWebinarSettingsAction } from '../Reducks/AccountSettings/actions';
import { getUserAction } from '../Reducks/User/actions';
import { reportAppcuesEventAction } from '../Reducks/Appcues/actions';
import { scheduleWebinarAction } from '../Reducks/ScheduleWebinar/actions';
import { scheduleVirtualEventAction } from '../Reducks/VirtualEventsSchedule/actions';
import { startMode } from '../Components/Card/Events/startWebinarHelper';
import { startWebinarAction, fetchWebinars, getWebinarAttendanceAction } from '../Reducks/Webinar/actions';
import { pendoUpdateOptions } from '../Pendo/pendo';
import { getWebinars } from '../Api/Webinar/webinarService';

async function getWebinarCount() {
  return getWebinars({ experienceType: 'all', includes: 'activesessionsinfo' });
}

const logger = appLogger.create('appcuesMiddleware');

function getAccountsAgeInDays(date) {
  const end = moment(date);
  const start = moment();
  return Math.round(moment.duration(start.diff(end)).asDays());
}

const appCuesIdentify = (userKey, value) => {
  try {
    window.Appcues.identify(userKey, value);
  } catch (error) {
    logger.error('Error on Appcues identify', error);
  }
};

const appcuesPage = () => {
  try {
    window.Appcues.page();
  } catch (error) {
    logger.error('Error on Appcues page', error);
  }
};

const supportedEvents = [
  'flow_started',
  'flow_completed',
  'flow_skipped',
  'step_started',
  'step_completed',
  'step_skipped',
  'step_interacted',
  'form_submitted',
  'form_field_submitted'
];

const setupTracking = (dispatch) => {
  if (window.Appcues) {
    window.Appcues.on('all', (name, event) => {
      if (supportedEvents.includes(name)) {
        dispatch(reportAppcuesEventAction(name, event));
      }
    });
  }
};

export const getAppcues = (state) => {
  if (state && state.appState && state.appState.appcues) {
    return state.appState.appcues;
  }
  return {};
};

export const setAppcues = (state, data) => {
  if (state && state.appState && state.appState.appcues) {
    return {
      appcues: {
        ...state.appState.appcues,
        ...data
      }
    };
  }
  return {
    appcues: {
      ...data
    }
  };
};

/* eslint-disable default-case */
const appcuesMiddleware = ({ getState, dispatch }) => (next) => (action) => {
  switch (action.type) {
    case LOCATION_CHANGE: {
      appcuesPage();
      break;
    }
    case getUserAction.fulfilled.toString(): {
      if (action.payload && action.payload.metadata && action.payload.user) {
        const metadata = action.payload.metadata;
        const user = action.payload.user;
        const accountAgeDays = getAccountsAgeInDays(user.meta.created);
        const newUser = (moment(user.meta.created)).isAfter(moment('2018-09-04T07:00:00.000Z')); // 9/4/2018 e2e was ga
        const userObj = {
          accountKey: metadata.accountKey,
          seats: metadata.seats,
          plan: metadata.description,
          type: metadata.channel,
          language: user.locale,
          createdAtDate: user.meta.created,
          accountAgeDays,
          newUser,
          serviceType: metadata.serviceType
        };
        appCuesIdentify(metadata.userKey, userObj);
        setupTracking(dispatch);
      }
      break;
    }

    case getApplicationState.fulfilled.toString():
    case setApplicationState.fulfilled.toString(): {
      let appState = { ...action.payload };
      if (action.type === getApplicationState.fulfilled.toString()) {
        appState = action.payload.data;
      }
      const userKey = getState().user.metadata.userKey;
      if (appState && appState.appcues && Object.keys(appState.appcues).length > 0) {
        const completed = appState.onboarding && appState.onboarding.onboardingTipsIndex && appState.onboarding.onboardingTipsIndex === -1;
        Object.assign(appState.appcues, { oldOnBoardingCompleted: completed });
        appCuesIdentify(userKey, appState.appcues);
      }
      break;
    }

    case 'SET_FLAGS': {
      const state = getState();
      if (state && state.user && state.user.metadata && state.user.metadata.userKey && action.data && action.data.isLDReady) {
        const userKey = state.user.metadata.userKey;
        appCuesIdentify(userKey, { scheduleV2Enabled: action.data.schedulingV2Enabled });
        setTimeout(() => {
          pendoUpdateOptions('g2w_schedledV2Enabled', action.data.schedulingV2Enabled);
        }, 5000);
      }

      break;
    }
    case fetchWebinars.fulfilled.toString(): {
      if (action.meta && action.meta.id === 'upcoming') {
        const state = getState();
        const upcomingWebinarsCount = get((action), (a) => a.payload.entities.pagination.webinars.pageSize);
        const userKey = get((state), (s) => s.user.metadata.userKey);
        const data = setAppcues(state, { upcomingWebinarsCount });
        appCuesIdentify(userKey, { upcomingWebinarsCount });
        dispatch(setApplicationState(data));
      }
      break;
    }

    case startWebinarAction.complete.toString(): {
      const state = getState();
      const isPractice = action.mode === startMode.PRACTICE;
      let data = null;
      if (getAppcues(state).hasRunPracticeSession !== true && isPractice) {
        data = setAppcues(state, { hasRunPracticeSession: true });
        setTimeout(() => {
          pendoUpdateOptions('g2w_hasRunPracticeSession', true);
        }, 5000);
      }
      if (getAppcues(state).hasRunLiveSession !== true && !isPractice) {
        data = setAppcues(state, { hasRunLiveSession: true });
        setTimeout(() => {
          pendoUpdateOptions('g2w_hasRunLiveSession', true);
        }, 5000);
      }
      if (data) {
        dispatch(setApplicationState(data));
      }

      break;
    }

    case scheduleWebinarAction.complete.toString(): {
      const state = getState();
      getWebinarCount().then((res) => {
        const totalWebinars = res && res.total ? res.total : 0;
        setTimeout(() => {
          pendoUpdateOptions('g2w_upcomingWebinarsCount', totalWebinars);
        }, 5000);
      });
      if (getAppcues(state).hasScheduledWebinar !== true) {
        const data = setAppcues(state, { hasScheduledWebinar: true });
        setTimeout(() => {
          pendoUpdateOptions('g2w_hasScheduledWebinar', true);
        }, 5000);
        dispatch(setApplicationState(data));
      }

      break;
    }

    case scheduleVirtualEventAction.complete.toString(): {
      const state = getState();
      if (getAppcues(state).hasScheduledWebinar !== true) {
        const data = setAppcues(state, { hasScheduledWebinar: true });
        setTimeout(() => {
          pendoUpdateOptions('g2w_hasScheduledWebinar', true);
        }, 5000);
        dispatch(setApplicationState(data));
      }

      break;
    }

    case createAndUpdateChannelAction.fulfilled.toString(): {
      const state = getState();
      if (getAppcues(state).hasCreatedChannel !== true) {
        const data = setAppcues(state, { hasCreatedChannel: true });
        setTimeout(() => {
          pendoUpdateOptions('g2w_hasCreatedChannel', true);
        }, 5000);
        dispatch(setApplicationState(data));
      }

      break;
    }

    case publishVideoToChannelAction.fulfilled.toString(): {
      const state = getState();
      if (getAppcues(state).hasPublishedVideo !== true) {
        const data = setAppcues(state, { hasPublishedVideo: true });
        setTimeout(() => {
          pendoUpdateOptions('g2w_hasPublishedVideo', true);
        }, 5000);
        dispatch(setApplicationState(data));
      }
      break;
    }
    case getOrganizerWebinarSettingsAction.fulfilled.toString(): {
      const state = getState();
      if (state && state.user && state.user.metadata && state.user.metadata.userKey && action.payload) {
        appCuesIdentify(state.user.metadata.userKey, { isTranscriptsEnabled: action.payload.transcriptsEnabled || false });
        setTimeout(() => {
          pendoUpdateOptions('g2w_isTranscriptsEnabled', action.payload.transcriptsEnabled || false);
        }, 5000);
      }
      break;
    }

    case getMessagesByTag.fulfilled.toString(): {
      const state = getState();
      const tag = action.payload.tag;
      const messages = action.payload.data;
      if (tag === 'RegistrationLimitReached' && messages.length > 0) {
        const data = setAppcues(state, { registrationLimitReached: true });
        appCuesIdentify(state.user.metadata.userKey, { registrationLimitReached: true });
        setTimeout(() => {
          pendoUpdateOptions('g2w_registrationLimitReached', true);
        }, 5000);
        dispatch(setApplicationState(data));
      }
      break;
    }

    case getWebinarAttendanceAction.complete.toString(): {
      const state = getState();
      const { attendanceStats } = action;
      const { user } = state;
      const { metadata } = user || {};
      const { maxAttendees, userKey } = metadata || {};
      const attendeeCount = (attendanceStats && attendanceStats.attendeeCount) || 0;
      if (maxAttendees) {
        const ratio = (attendeeCount / maxAttendees) * 100;
        if (ratio >= 75) {
          const data = setAppcues(state, { attendeeLimitReached: true });
          appCuesIdentify(userKey, { attendeeLimitReached: true });
          setTimeout(() => {
            pendoUpdateOptions('g2w_attendeeLimitReached', true);
          }, 4000);
          dispatch(setApplicationState(data));
        }
      }
      break;
    }
  }
  return next(action);
};

export default appcuesMiddleware;
